import { StoreCustomerAddress } from "@medusajs/types";
import { getAddressMetadata } from "@/lib/utils";
import ConfirmDelete from "@/components/molecules/confirm-delete";
import { Locales } from "@/lib/i18n";
import { sdk } from "@/lib/medusa/client";
import { useCustomer } from "@/hooks/useCustomer";
import { useState, useTransition } from "react";
import { toast } from "@/components/atoms/toast";
import { __ } from "@/lib/i18n/functions";
import ModalTemplate from "@/components/molecules/modal-template";
import BillingAddressForm from "@/components/organisms/forms/checkout/billing-address-form";
import { DeliveryAndBillingSchema } from "@/lib/validation/payment";
import { UseFormSetValue } from "react-hook-form";
import { useCheckoutStore } from "@/stores/checkout-store";

type BillingAddressCardProps = {
  address: StoreCustomerAddress;
  locale: Locales;
  type: "checkout" | "account";
  setValue?: UseFormSetValue<DeliveryAndBillingSchema>;
};

const BillingAddressCard = ({ address, locale, type, setValue }: BillingAddressCardProps) => {
  const setData = useCheckoutStore((state) => state.setData);
  const metadata = getAddressMetadata(address);
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { refreshCustomer } = useCustomer();

  const handleDeleteAddress = (addressId: string) => {
    startTransition(async () => {
      try {
        await sdk.store.customer.deleteAddress(addressId);
        await refreshCustomer();
        if (type === "checkout" && setValue) {
          setValue("billingAddress", "");
          setData({ billingAddress: "" });
        }
        toast.success({ title: __("Address deleted successfully", locale) });
      } catch (error) {
        toast.error({
          title: __("Error deleting address", locale),
          description: __("Please try again later.", locale),
        });
        console.error("Error deleting address:", error);
      } finally {
        setIsOpen(false);
      }
    });
  };

  return (
    <div className="border-primary-light/30 space-y-2.5 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between">
        {(address.address_name || metadata.address_name) && (
          <h4 className="font-cabinet-grotesk text-xl font-bold">{address.address_name || metadata.address_name}</h4>
        )}
        <div className="flex items-center gap-2.5">
          <ConfirmDelete
            open={isOpen}
            onOpenChange={setIsOpen}
            onClick={() => handleDeleteAddress(address.id)}
            isActionHappening={isPending}
            locale={locale}
            text={__("Are you sure you want to delete this address?", locale)}
            modalTrigger={<button className="cursor-pointer underline">{__("Delete", locale)}</button>}
            title={__("Delete address", locale)}
          />
          <ModalTemplate
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            className="sm:max-w-[920px]"
            classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
            title={__("Edit billing address", locale)}
            triggerAsChild
            modalTrigger={<button className="cursor-pointer underline">{__("Edit", locale)}</button>}
          >
            <BillingAddressForm
              accountTypeValue={address.company ? "company" : "individual"}
              locale={locale}
              onSuccess={() => {
                setIsModalOpen(false);
              }}
              addressToEdit={address}
              addressId={address.id}
            />
          </ModalTemplate>
        </div>
      </div>
      <dl className="space-y-2.5">
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <div className="w-fit space-y-2.5">
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("ZIP Code", locale)} :</dt>
              <dd className="font-semibold">{address.postal_code}</dd>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("City", locale)} :</dt>
              <dd className="font-semibold">{address.city}</dd>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Contact", locale)} :</dt>
              <dd className="font-semibold">{metadata.contact_person}</dd>
            </div>
          </div>
          <div className="w-fit space-y-2.5">
            {address.company && (
              <div className="flex flex-wrap gap-1.5">
                <dt>{__("Company", locale)} :</dt>
                <dd className="font-semibold">{address.company}</dd>
              </div>
            )}
            {metadata.vat_number && (
              <div className="flex flex-wrap gap-1.5">
                <dt>{__("VAT Number", locale)} :</dt>
                <dd className="font-semibold">{metadata.vat_number}</dd>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <dt>{__("Postal Address", locale)} :</dt>
          <dd className="font-semibold">{address.address_1}</dd>
        </div>
      </dl>
    </div>
  );
};

export default BillingAddressCard;
